import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Thermometer, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { vehicleActivityAPI } from '../../../lib/api';

const DAY = 86400000;
const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY)) : null);

/**
 * Inventory Sales Temperature — Tap-Intelligence action scoring.
 * Reuses the EXISTING vehicle-activity inventory dataset (no new intelligence
 * architecture). Computes a 0-100 action score per active vehicle from existing
 * signals (aging, staleness, engagement, conversion); higher = colder/priority.
 */
export function computeTemp(v) {
    const dim = daysSince(v.created_at);                 // days in inventory
    const dsa = v.last_activity ? daysSince(v.last_activity) : (dim ?? 30); // days since activity
    const total = v.total || 0;
    const conv = (v.test_drives || 0) + (v.questions || 0);
    const taps = v.taps || 0;

    const aging = Math.min((dim ?? 30) / 60, 1) * 40;            // older inventory ↑
    const stale = Math.min(dsa / 21, 1) * 30;                    // no recent activity ↑
    const lowEng = (1 - Math.min(total / 20, 1)) * 20;           // few interactions ↑
    const weakConv = (1 - Math.min(conv / Math.max(taps, 1), 1)) * 10; // low tap→lead ↑
    const raw = aging + stale + lowEng + weakConv;               // 0-100, higher = worse
    // Displayed action score: 0 = MAXIMUM action required (coldest) · 100 = healthiest.
    const score = Math.round(100 - raw);

    const temp = score <= 40 ? 'red' : score <= 65 ? 'yellow' : 'green';
    const contributors = [
        ['aging', aging, 'Aging inventory'],
        ['stale', stale, 'No recent activity'],
        ['lowEng', lowEng, 'Low engagement'],
        ['weakConv', weakConv, 'Weak lead conversion'],
    ].sort((a, b) => b[1] - a[1]);
    const reason = temp === 'green' ? 'Healthy engagement' : contributors[0][2];
    const action = {
        'Aging inventory': 'Reprice / feature this unit; refresh photos & description.',
        'No recent activity': 'Promote on the lot & socials; add a special offer.',
        'Low engagement': 'Push to hot leads and run a targeted campaign.',
        'Weak lead conversion': 'Have a salesperson proactively follow up on taps.',
        'Healthy engagement': 'Keep momentum — prioritise follow-ups on live leads.',
    }[reason];
    return { ...v, dim, dsa, score, temp, reason, action };
}

const TEMP_META = {
    red: { dot: 'bg-red-500', ring: 'border-red-500/40 bg-red-500/5', label: 'Cold · Action required', text: 'text-red-400' },
    yellow: { dot: 'bg-amber-400', ring: 'border-amber-400/40 bg-amber-400/5', label: 'Watch · Attention', text: 'text-amber-300' },
    green: { dot: 'bg-emerald-500', ring: 'border-emerald-500/40 bg-emerald-500/5', label: 'Healthy · Performing', text: 'text-emerald-400' },
};

export function AutomotiveSalesTempDialog({ open, onOpenChange, organizationId, locationIds, onDispatch }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    const load = useCallback(async () => {
        if (!open || !organizationId) return;
        setLoading(true);
        try {
            const r = await vehicleActivityAPI.inventory(organizationId, 30, locationIds);
            setRows((r.data || []).map(computeTemp).sort((a, b) => a.score - b.score));
        } finally { setLoading(false); }
    }, [open, organizationId, locationIds]);
    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => ({
        red: rows.filter((r) => r.temp === 'red').length,
        yellow: rows.filter((r) => r.temp === 'yellow').length,
        green: rows.filter((r) => r.temp === 'green').length,
    }), [rows]);
    const shown = filter === 'all' ? rows : rows.filter((r) => r.temp === filter);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="cc-sales-temp-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Thermometer className="w-5 h-5 text-amber-400" />{t('commandCenter.automotive.salesTemp', 'Inventory Sales Temperature')}</DialogTitle>
                    <DialogDescription>{t('commandCenter.automotive.salesTempSub', 'Which vehicles need attention most right now — and why.')}</DialogDescription>
                    <p className="text-xs text-slate-400 mt-1" data-testid="cc-temp-legend">{t('commandCenter.automotive.scoreLegend', 'Action score: 0 = act now (coldest) → 100 = healthy. Coldest vehicles are listed first.')}</p>
                </DialogHeader>

                <div className="flex flex-wrap gap-2 mb-3">
                    {[['all', `All (${rows.length})`], ['red', `🔴 ${counts.red}`], ['yellow', `🟡 ${counts.yellow}`], ['green', `🟢 ${counts.green}`]].map(([k, lbl]) => (
                        <button key={k} onClick={() => setFilter(k)} data-testid={`cc-temp-filter-${k}`}
                            className={`px-3 py-1.5 rounded-full text-xs border ${filter === k ? 'bg-slate-700 border-slate-500 text-white' : 'border-slate-700 text-slate-300'}`}>{lbl}</button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : shown.length === 0 ? (
                    <p className="py-8 text-center text-slate-400 text-sm" data-testid="cc-temp-empty">{t('commandCenter.automotive.noVehicles', 'No active vehicles in scope.')}</p>
                ) : (
                    <div className="space-y-2">
                        {shown.map((r) => {
                            const m = TEMP_META[r.temp];
                            return (
                                <div key={r.vehicle_id} className={`rounded-xl border p-3 ${m.ring}`} data-testid={`cc-temp-row-${r.stock_number}`}>
                                    <div className="flex items-start gap-3">
                                        <span className={`w-3 h-3 rounded-full mt-1 shrink-0 ${m.dot}`} data-testid={`cc-temp-dot-${r.temp}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <p className="text-sm font-semibold text-white">{[r.year, r.make, r.model].filter(Boolean).join(' ')} <span className="text-slate-400 font-normal">#{r.stock_number}</span></p>
                                                <span className={`text-xs font-semibold ${m.text}`}>{m.label} · {t('commandCenter.automotive.score', 'Score')} {r.score}</span>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-400">
                                                <span>{t('commandCenter.automotive.daysInInv', 'Days in inv.')}: <b className="text-slate-200">{r.dim ?? '—'}</b></span>
                                                <span>{t('commandCenter.automotive.lastActivity', 'Last activity')}: <b className="text-slate-200">{r.dsa != null ? `${r.dsa}d` : '—'}</b></span>
                                                <span>{t('commandCenter.automotive.visits', 'Visits')}: <b className="text-slate-200">{r.taps}</b></span>
                                                <span>{t('commandCenter.automotive.testDrives', 'Test drives')}: <b className="text-slate-200">{r.test_drives}</b></span>
                                                <span>{t('commandCenter.automotive.saves', 'Saves')}: <b className="text-slate-200">{r.saves}</b></span>
                                            </div>
                                            <p className="text-xs text-slate-300 mt-1.5"><b className={m.text}>{r.reason}.</b> {r.action}</p>
                                        </div>
                                        {onDispatch && (
                                            <Button size="sm" variant="outline" data-testid={`cc-temp-dispatch-${r.stock_number}`}
                                                onClick={() => { onOpenChange(false); onDispatch(r); }}
                                                className="h-8 px-2 text-xs border-amber-600/50 text-amber-300 hover:bg-amber-500/10 shrink-0">
                                                <Send className="w-3.5 h-3.5 mr-1" />{t('commandCenter.automotive.dispatch', 'Dispatch')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
